import React from 'react';
import { Check, Users } from 'lucide-react';
import { GenericListView, PatientAvatar, SamplePill, StatusPill, getStatusConfig } from '../common';
import { PatientData } from '../../types/admin/patient';
import type { PatientStatusWire } from '../../dtos/admin/patient.dto';
import { PATIENT_STATUS_FILTERS } from '../../constants/patientLifecycle';
import PayerBadges from './insurance/PayerBadges';
import RecertOverdueBadge from './RecertOverdueBadge';
import UnsyncedWorkHint from '../offline/UnsyncedWorkHint';

interface AllPatientsListProps {
  patients: PatientData[];
  selectedPatientId: number | string | null;
  expanded: boolean;
  onToggleExpand: () => void;
  loading: boolean;
  error: string | null;
  /** More results are available from the server ("Load More", PAT-08) */
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: () => void;
  totalCount?: number;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  /** Selected status chips (wire values) */
  statusFilter: PatientStatusWire[];
  onStatusFilterChange: (statuses: PatientStatusWire[]) => void;
  onRefresh: () => void;
  onDismissError: () => void;
  onSelectPatient: (patient: PatientData | null) => void;
  onShowPatientChart?: (patient: PatientData) => void;
  /** Notices above the list (Phase 13: patient cap banner) */
  topBanner?: React.ReactNode;
  /** Call to action in the empty state (Phase 13: "Load sample patient") */
  emptyStateAction?: React.ReactNode;
}

const formatDate = (dateString: string) => {
  if (!dateString) return '';
  const [year, month, dayPart] = dateString.split('-');
  return `${month?.padStart(2, '0')}/${dayPart?.substring(0, 2).padStart(2, '0')}/${year}`;
};

const ALL_STATUSES = PATIENT_STATUS_FILTERS.map((f) => f.value);

/** Status filter chips: multi-select; "All" selects every status. At least one stays selected. */
export const StatusFilterChips: React.FC<{
  value: PatientStatusWire[];
  onChange: (statuses: PatientStatusWire[]) => void;
}> = ({ value, onChange }) => {
  const allSelected = ALL_STATUSES.every((s) => value.includes(s));
  const chipClass = (active: boolean) =>
    `px-3 py-1 rounded-full border text-xs font-medium transition-colors min-h-[32px] ${
      active ? 'bg-slate-800 border-slate-800 text-white' : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-100'
    }`;
  const toggle = (status: PatientStatusWire) => {
    if (value.includes(status)) {
      if (value.length === 1) return;
      onChange(value.filter((s) => s !== status));
    } else {
      onChange(ALL_STATUSES.filter((s) => s === status || value.includes(s)));
    }
  };
  return (
    <div className="flex flex-wrap gap-2 mb-3" role="group" aria-label="Filter patients by status">
      {PATIENT_STATUS_FILTERS.map((filter) => {
        const active = value.includes(filter.value);
        return (
          <button key={filter.value} type="button" aria-pressed={active} className={chipClass(active)} onClick={() => toggle(filter.value)}>
            {filter.label}
          </button>
        );
      })}
      <button type="button" aria-pressed={allSelected} className={chipClass(allSelected)} onClick={() => onChange([...ALL_STATUSES])}>
        All
      </button>
    </div>
  );
};

const AllPatientsList: React.FC<AllPatientsListProps> = ({
  patients,
  selectedPatientId,
  expanded,
  onToggleExpand,
  loading,
  error,
  hasMore,
  loadingMore,
  onLoadMore,
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  onRefresh,
  onDismissError,
  onSelectPatient,
  onShowPatientChart,
  topBanner,
  emptyStateAction,
}) => {
  const selectPatient = (patient: PatientData) => {
    onSelectPatient(selectedPatientId === patient.id ? null : patient);
  };

  const showPatientChart = (patient: PatientData) => {
    onSelectPatient(patient);
    onShowPatientChart?.(patient);
  };

  return (
    <>
    {/* Phase 16C C10 */}
    <UnsyncedWorkHint className="mb-2" />
    <GenericListView
      title="All Patients"
      icon={<Users size={20} />}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      items={patients}
      keyExtractor={(patient) => patient.id.toString()}
      columns={[
        {
          key: 'patient',
          label: 'Patient',
          width: 'flex-1 min-w-0 max-w-[45%]',
          render: (patient) => {
            const name = `${patient.firstName} ${patient.lastName}`;
            return (
              <div className="flex items-center gap-3">
                <PatientAvatar
                  patientId={patient.id.toString()}
                  name={name}
                  profilePictureUrl={patient.profilePictureUrl}
                />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{name}</div>
                  <div className="text-xs text-gray-500 truncate">MRN: {patient.mrn}</div>
                  <PayerBadges summary={patient.insuranceSummary} className="mt-0.5" />
                  <RecertOverdueBadge overdue={patient.recertOverdue} className="mt-0.5" />
                </div>
              </div>
            );
          },
        },
        {
          key: 'dob',
          label: 'DOB',
          width: 'w-24',
          align: 'center' as const,
          render: (patient) => <span className="text-sm text-gray-500">{formatDate(patient.dob)}</span>,
        },
        {
          key: 'gender',
          label: 'Gender',
          width: 'w-24',
          align: 'center' as const,
          render: (patient) => <span className="text-sm text-gray-500">{patient.gender}</span>,
        },
        {
          key: 'status',
          label: 'Status',
          width: 'w-24',
          align: 'center' as const,
          render: (patient) => (
            <span className="inline-flex flex-wrap items-center justify-center gap-1">
              <StatusPill status={patient.status} />
              {patient.isSample && <SamplePill />}
            </span>
          ),
        },
      ]}
      actions={[
        {
          icon: (patient) => (
            <span className="flex items-center gap-1 whitespace-nowrap">
              <Check size={14} />
              {selectedPatientId === patient.id ? 'Unselect' : 'Select'}
            </span>
          ),
          label: (patient) => selectedPatientId === patient.id ? 'Unselect patient' : 'Select patient',
          onClick: selectPatient,
          className: 'px-2 py-1 text-sm bg-slate-200 text-slate-700 hover:bg-slate-300 rounded transition-colors whitespace-nowrap',
        },
        {
          icon: (
            <span className="flex items-center gap-1 whitespace-nowrap">
              <img src={require('../../assets/images/icon_dark.png')} alt="" className="h-4 w-4" />
              Care
            </span>
          ),
          label: 'View patient chart',
          onClick: showPatientChart,
          className: 'px-2 py-1 text-sm bg-slate-200 text-slate-700 hover:bg-slate-300 rounded transition-colors whitespace-nowrap',
        },
      ]}
      actionsWidth="w-44"
      isItemSelected={(patient) => selectedPatientId === patient.id}
      mobileCard={{
        avatar: (patient) => (
          <PatientAvatar
            patientId={patient.id.toString()}
            name={`${patient.firstName} ${patient.lastName}`}
            profilePictureUrl={patient.profilePictureUrl}
            size="lg"
          />
        ),
        primaryText: (patient) => `${patient.firstName} ${patient.lastName}`,
        secondaryText: (patient) => `${formatDate(patient.dob)} • ${patient.gender} • ${getStatusConfig(patient.status).label}`,
        tertiaryText: (patient) => `MRN: ${patient.mrn}`,
        badges: (patient) => [
          ...(patient.isSample ? [{ label: 'Sample', className: 'bg-violet-100 text-violet-800 border border-violet-300' }] : []),
          ...(patient.recertOverdue ? [{ label: 'Recert overdue', className: 'bg-red-100 text-red-800 border border-red-300' }] : []),
        ],
        actions: (patient) => [
          {
            icon: (
              <span className="flex items-center gap-1">
                <Check size={18} />
                {selectedPatientId === patient.id ? 'Unselect' : 'Select'}
              </span>
            ),
            label: selectedPatientId === patient.id ? 'Unselect' : 'Select',
            onClick: () => selectPatient(patient),
          },
          {
            icon: (
              <span className="flex items-center gap-1">
                <img src={require('../../assets/images/icon_dark.png')} alt="" className="h-5 w-5" />
                Care
              </span>
            ),
            label: 'Care',
            onClick: () => showPatientChart(patient),
          },
        ],
      }}
      onRefresh={onRefresh}
      loading={loading}
      showNew={false}
      alertBanner={expanded ? (
        <>
          {topBanner && <div className="mb-3">{topBanner}</div>}
          <StatusFilterChips value={statusFilter} onChange={onStatusFilterChange} />
        </>
      ) : undefined}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      loadingMore={loadingMore}
      error={error}
      onDismissError={onDismissError}
      hasLoadedOnce={!loading || patients.length > 0}
      emptyStateIcon={<Users size={48} className="text-gray-300" />}
      emptyStateTitle="No patients found"
      emptyStateMessage={searchQuery ? 'Try adjusting your search or status filters.' : 'No patients match the selected statuses.'}
      emptyStateAction={emptyStateAction}
      searchValue={searchQuery}
      onSearchChange={onSearchChange}
      searchPlaceholder="Search by name or MRN..."
      showFilters={false}
      desktopInlineFilters
      hideDesktopFilterResultCount
    />
    </>
  );
};

export default AllPatientsList;
